import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  styled, 
  TextField, 
  IconButton, 
  Grid, 
  Card, 
  CardContent, 
  Menu, 
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Button
} from '@mui/material';
import { 
  Mic, 
  Send, 
  AspectRatio, 
  Timer, 
  Check, 
  Download,
  Collections,
  PlayArrow,
  Fullscreen,
  Delete
} from '@mui/icons-material';
import {useVideoHistoryQuery} from '@/features/videos/hooks/useVideosQuery';
import {useGenerateVideoMutation, useDeleteVideoMutation} from '@/features/videos/hooks/useVideosMutations';
import ResponsivePage from '@/components/ResponsivePage';

// Styled components
const FloatingInput = styled(Paper)({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  background: '#fff',
  maxWidth: '800px',
  width: '100%',
  margin: '0 auto',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
  borderRadius: '12px',
  flexDirection: 'column',
});

const SampleCard = styled(Card)({
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
  },
});

// Sample prompts data
const samplePrompts = [
  {
    title: 'Abstract Art',
    description: 'Colorful abstract shapes morphing and flowing',
    icon: '🎨'
  },
  {
    title: 'Ocean Waves',
    description: 'Peaceful ocean waves crashing on a beach',
    icon: '🌊'
  },
];

// Video dimension options
const dimensionOptions = [
  { value: '1080x1080', label: '1080x1080 (Square)', selected: true },
  { value: '1920x1080', label: '1920x1080 (Landscape)', selected: false },
  { value: '1080x1920', label: '1080x1920 (Portrait)', selected: false }
];

// Duration options
const durationOptions = [
  { value: '3', label: '3 seconds', selected: false },
  { value: '5', label: '5 seconds', selected: true },
  { value: '10', label: '10 seconds', selected: false },
  { value: '15', label: '15 seconds', selected: false }
];

const GenerateVideoPage: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [lastPrompt, setLastPrompt] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  //const [generating, setGenerating] = useState(false);
  const [generatedVideo, setGeneratedVideo] = useState<string | null>(null);
  //const [error, setError] = useState<string | null>(null);
  
  const generateVideoMutation = useGenerateVideoMutation()

  // Menu state
  const [dimensionsAnchorEl, setDimensionsAnchorEl] = useState<null | HTMLElement>(null);
  const [durationAnchorEl, setDurationAnchorEl] = useState<null | HTMLElement>(null);
  
  // Selected options
  const [selectedDimension, setSelectedDimension] = useState(dimensionOptions.find(o => o.selected)?.value || '1080x1080');
  const [selectedDuration, setSelectedDuration] = useState(durationOptions.find(o => o.selected)?.value || '5');
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Video history state
  const {data: videoHistory, isLoading: loadingHistory} = useVideoHistoryQuery()
  
  // Delete video mutation
  const deleteVideoMutation = useDeleteVideoMutation();
  
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Speech recognition
  const handleMicClick = () => {
    setIsRecording(!isRecording);
    // Implement speech recognition logic here
  };

  // Sample prompts
  const handleSampleClick = (description: string) => {
    setPrompt(description);
  };

  // Generate video
  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    
    try {
      // Parse dimensions
      const [width, height] = selectedDimension.split('x').map(Number);
      
      const result = await generateVideoMutation.mutateAsync({
        prompt,
        width: width,
        height: height,
        n_seconds: parseInt(selectedDuration),
        n_variants: 1
      })       
      
      if (result.success && result.url) {
        setGeneratedVideo(result.url);
        setLastPrompt(prompt); // Store the prompt used for this video
      } else {
        setLastPrompt(null);
      }
    } catch (err) {
      console.error('Error generating video:', err);
      setLastPrompt(null);
    }
  };

  // Menu handlers
  const handleDimensionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setDimensionsAnchorEl(event.currentTarget);
  };
  
  const handleDurationClick = (event: React.MouseEvent<HTMLElement>) => {
    setDurationAnchorEl(event.currentTarget);
  };

  const handleDimensionsClose = () => {
    setDimensionsAnchorEl(null);
  };
  
  const handleDurationClose = () => {
    setDurationAnchorEl(null);
  };

  const handleDimensionSelect = (dimension: string) => {
    setSelectedDimension(dimension);
    handleDimensionsClose();
  };
  
  const handleDurationSelect = (duration: string) => {
    setSelectedDuration(duration);
    handleDurationClose();
  };

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDownload = async () => {
    if (!generatedVideo) return;

    try {
      const response = await fetch(generatedVideo);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'generated_video.mp4');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Error downloading video:', downloadError);
    }
  };

  return (
    <ResponsivePage
      alignItems="center"
      position="relative"
      sx={{
        bgcolor: 'white',
        backdropFilter: 'blur(10px)',
        pb: 6,
      }}
    >
      
      <Box 
        sx={{ 
          textAlign: 'center', 
          py: 4,
          width: '100%',
          maxWidth: 1200,
          mx: 'auto'
        }}
      >
        <Typography variant="h4" fontWeight={700} color="primary.main">
          GENERATE VIDEOS
        </Typography>
        
        {/* Tab Navigation */}
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          centered
          sx={{ mt: 2, mb: 1 }}
        >
          <Tab 
            icon={<Send fontSize="small" />} 
            label="Generate New" 
            iconPosition="start"
          />
          <Tab 
            icon={<Collections fontSize="small" />} 
            label="Past Videos" 
            iconPosition="start"
          />
        </Tabs>
        
        {tabValue === 0 && (
          <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
            Create videos with AI using text prompts
          </Typography>
        )}
      </Box>

      {/* Generated Video Display - Only show in Generate New tab */}
      {generatedVideo && tabValue === 0 && (
        <Paper 
          elevation={3}
          sx={{ 
            mb: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
            width: '100%',
            maxWidth: { xs: '100%', sm: 760, md: 980 },
            overflow: 'hidden',
            mx: 'auto',
            p: { xs: 1, sm: 1.5 },
            gap: { xs: 1, md: 2 },
            borderRadius: 2,
          }}
        >
          {/* Video on the left */}
          <Box 
            component="video" 
            src={generatedVideo} 
            controls
            preload="metadata"
            onError={(e) => {
              console.error('Video error:', e);
              console.error('Video URL:', generatedVideo);
            }}
            onLoadStart={() => {
              console.debug('Video loading started');
            }}
            onCanPlay={() => {
              console.debug('Video can play');
            }}
            sx={{
              width: { xs: '100%', md: '62%' },
              height: 'auto',
              maxHeight: { xs: 240, sm: 280, md: 240 },
              objectFit: 'contain',
              borderRadius: 1,
              mr: { xs: 0, md: 1 },
              backgroundColor: '#f5f5f5',
            }}
          />
          {/* Text content and Download button on the right */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'flex-start', 
            width: { xs: '100%', md: '38%' },
            overflowY: 'visible',
            pt: { xs: 0.5, md: 1 },
          }}>
            {lastPrompt && (
              <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 600, color: 'text.primary', textAlign: 'left' }}>
                {lastPrompt.substring(0, 15)}
                {lastPrompt.length > 15 ? '...' : ''}
              </Typography>
            )}
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', textAlign: 'left' }}>
              Generated with {selectedDimension} resolution, {selectedDuration}s duration.
            </Typography>
            {lastPrompt && (
              <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', fontStyle: 'italic', color: 'text.secondary', textAlign: 'left' }}>
                Prompt: "{lastPrompt}"
              </Typography>
            )}
            <Box sx={{ mt: 1, textAlign: 'left' }}>
              <Tooltip title="Download Video (MP4)">
                <IconButton onClick={handleDownload} color="primary" size="small">
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      )}

     {/* Tab Content */}
      {tabValue === 0 ? (
        <>
          {/* Generate Tab Content */}
          {/* Conditionally render Sample Prompts */}
          {!generatedVideo && (
            <Grid container spacing={3} sx={{ mb: 4, width: '100%', maxWidth: 1200, mx: 'auto' }}>
              {samplePrompts.map((sample, index) => (
                <Grid key={index} size={{ md: 6, sm: 6, xs:12}}>
                  <SampleCard 
                    onClick={() => handleSampleClick(sample.description)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent>
                      <Box display="flex" mb={1}>
                        <Typography variant="h1" sx={{ mr: 1, fontSize: 24 }}>
                          {sample.icon}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {sample.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {sample.description}
                      </Typography>
                    </CardContent>
                  </SampleCard>
                </Grid>
              ))}
            </Grid>      
          )}
        </>
      ) : (
        /* Past Videos Tab Content */
        <Box 
          sx={{ 
            width: '100%', 
            maxWidth: 1200, 
            mx: 'auto', 
            my: { xs: 2, md: 4 },
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            px: { xs: 1, sm: 2, md: 3 },
            py: { xs: 1.5, sm: 2, md: 3 },
            pb: { xs: 3, md: 6 },
          }}
        >
          {loadingHistory ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={5}>
              <CircularProgress />
            </Box>
          ) : videoHistory?.length === 0 ? (
            <Box textAlign="center" p={5}>
              <Typography variant="h6" color="text.secondary">
                No videos found in your history
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Generate your first video in the Generate tab
              </Typography>
            </Box>
          ) : (
            <ImageList 
              sx={{ 
                width: '100%', 
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(280px, 1fr))!important',
                  sm: 'repeat(auto-fill, minmax(320px, 1fr))!important',
                  md: 'repeat(auto-fill, minmax(350px, 1fr))!important'
                },
                gap: '24px!important',
                overflow: 'visible',
                pb: 4
              }}
              gap={24}
            >
              {videoHistory!.map((video) => (
                <ImageListItem 
                  key={video.id} 
                  sx={{ 
                    boxShadow: 2, 
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    mb: 3,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      zIndex: 1
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '56.25%', // 16:9 Aspect ratio
                      width: '100%',
                      height: 0,
                      overflow: 'hidden',
                      backgroundColor: 'rgba(0,0,0,0.05)'
                    }}
                  >
                    <video
                      src={video.url}
                      preload="metadata"
                      style={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.8)',
                          transform: 'translate(-50%, -50%) scale(1.1)',
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const videoElement = e.currentTarget.parentElement?.querySelector('video');
                        if (videoElement) {
                          if (videoElement.paused) {
                            videoElement.play();
                          } else {
                            videoElement.pause();
                          }
                        }
                      }}
                    >
                      <PlayArrow sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                  </Box>
                  <ImageListItemBar
                    title={
                      <Tooltip title={video.prompt}>
                        <Typography noWrap variant="subtitle2" sx={{ lineHeight: 1.3, fontWeight: 600 }}>
                          {video.prompt.substring(0, 15)}
                          {video.prompt.length > 15 ? '...' : ''}
                        </Typography>
                      </Tooltip>
                    }
                    subtitle={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" color="rgba(255,255,255,0.7)" sx={{ fontSize: '0.7rem' }}>
                          {new Date(video.created_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </Typography>
                        <Typography variant="caption" color="rgba(255,255,255,0.7)" sx={{ fontSize: '0.7rem' }}>
                          {video.n_seconds}s • {video.width}x{video.height}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      '& .MuiImageListItemBar-titleWrap': {
                        padding: '8px 12px',
                      },
                      background: 'linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.3), transparent)'
                    }}
                    actionIcon={
                      <Box sx={{ display: 'flex' }}>
                        {/* View full size button */}
                        <Tooltip title="Open in fullscreen">
                          <IconButton
                            sx={{ color: 'white' }}
                            aria-label={`open fullscreen video ${video.prompt}`}
                            onClick={() => {
                              window.open(video.url, '_blank');
                            }}
                          >
                            <Fullscreen fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {/* Download button */}
                        <Tooltip title="Download video">
                          <IconButton
                            sx={{ color: 'white', mr: 1 }}
                            aria-label={`download video ${video.prompt}`}
                            onClick={() => {
                              const downloadVideo = async () => {
                                try {
                                  const response = await fetch(video.url);
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  // Use first 20 chars of prompt as filename
                                  const filename = video.prompt.substring(0, 15).replace(/[^a-z0-9]/gi, '_').toLowerCase();
                                  link.setAttribute('download', `${filename}.mp4`);
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error('Error downloading video:', error);
                                }
                              };
                              downloadVideo();
                            }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Delete button */}
                        <Tooltip title="Delete video">
                          <IconButton
                            sx={{ color: 'white' }}
                            aria-label={`delete video ${video.prompt}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Open confirmation
                              setConfirmDeleteId(video.id);
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Box>
      )}
      
      {/* Input Area - Only show in Generate tab */}
      {tabValue === 0 && (
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleGenerate(); }}
      sx={{ 
        width: '100%', 
        maxWidth: 800, 
        mb: 4, 
        mx: 'auto',
        mt: generatedVideo ? '20vh' : 4 // Push down if video is generated
      }}>
        <FloatingInput>
          {/* Text Input */}
          <Box sx={{ width: '100%', p: 1 }}>   
            <TextField
              fullWidth
              multiline
              placeholder="Describe the video you want to generate..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              variant="standard"
              slotProps={{ input: { disableUnderline: true } }}
              sx={{ '& .MuiInputBase-root': { fontSize: '1.1rem' } }}
              rows={4}
            />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            width: '100%', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderTop: '1px solid rgba(0, 0, 0, 0.05)',
            paddingTop: 1,
            mt: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Voice Input */}
              <Tooltip title="Voice to text">
                <IconButton 
                  type="button" 
                  color="primary" 
                  onClick={handleMicClick}
                  disabled={generateVideoMutation.isPending}
                >
                  <Mic color={isRecording ? 'error' : 'inherit'} />
                </IconButton>
              </Tooltip>
              
              {/* Dimensions Dropdown */}
              <Tooltip title="Video dimensions">
                <IconButton
                  color="primary"
                  onClick={handleDimensionsClick}
                  disabled={generateVideoMutation.isPending}
                >
                  <AspectRatio />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={dimensionsAnchorEl}
                open={Boolean(dimensionsAnchorEl)}
                onClose={handleDimensionsClose}
                PaperProps={{
                  sx: {
                    width: 220,
                    maxWidth: '100%',
                  },
                }}
              >
                <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
                  Dimensions
                </Typography>
                {dimensionOptions.map((option) => (
                  <MenuItem 
                    key={option.value} 
                    onClick={() => handleDimensionSelect(option.value)}
                  >
                    {selectedDimension === option.value && (
                      <ListItemIcon>
                        <Check fontSize="small" />
                      </ListItemIcon>
                    )}
                    <ListItemText 
                      inset={selectedDimension !== option.value}
                      primary={option.label} 
                    />
                  </MenuItem>
                ))}
              </Menu>
              
              {/* Duration Dropdown */}
              <Tooltip title="Video duration">
                <IconButton
                  color="primary"
                  onClick={handleDurationClick}
                  disabled={generateVideoMutation.isPending}
                >
                  <Timer />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={durationAnchorEl}
                open={Boolean(durationAnchorEl)}
                onClose={handleDurationClose}
                PaperProps={{
                  sx: {
                    width: 180,
                    maxWidth: '100%',
                  },
                }}
              >
                <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
                  Duration
                </Typography>
                {durationOptions.map((option) => (
                  <MenuItem 
                    key={option.value} 
                    onClick={() => handleDurationSelect(option.value)}
                  >
                    {selectedDuration === option.value && (
                      <ListItemIcon>
                        <Check fontSize="small" />
                      </ListItemIcon>
                    )}
                    <ListItemText 
                      inset={selectedDuration !== option.value}
                      primary={option.label} 
                    />
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            
            {/* Generate Button */}
            <IconButton 
              type="submit" 
              color="primary" 
              disabled={!prompt.trim() || generateVideoMutation.isPending}
              sx={{ ml: 1 }}
            >
              {generateVideoMutation.isPending ? <CircularProgress size={24} /> : <Send />}
            </IconButton>
          </Box>
        </FloatingInput>
      </Box>
      )}
      
      {/* Error Snackbar */}
      <Snackbar 
        open={!!generateVideoMutation.error} 
        autoHideDuration={6000} 
        onClose={() => generateVideoMutation.reset()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => generateVideoMutation.reset()} severity="error" sx={{ width: '100%' }}>
          {generateVideoMutation.error?.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Snackbar/Dialog */}
      <Snackbar
        open={!!confirmDeleteId}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          action={
            <Box>
              <Button
                color="inherit"
                size="small"
                onClick={async () => {
                  if (!confirmDeleteId) return;
                  try {
                    await deleteVideoMutation.mutateAsync(confirmDeleteId);
                    setInfoMessage('Video deleted');
                  } catch (err) {
                    console.error('Failed to delete video', err);
                  } finally {
                    setConfirmDeleteId(null);
                  }
                }}
              >
                Delete
              </Button>
              <Button color="inherit" size="small" onClick={() => setConfirmDeleteId(null)}>
                Cancel
              </Button>
            </Box>
          }
        >
          Are you sure you want to delete this video from your history?
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!infoMessage}
        autoHideDuration={3000}
        onClose={() => setInfoMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setInfoMessage(null)} severity="success" sx={{ width: '100%' }}>
          {infoMessage}
        </Alert>
      </Snackbar>
    </ResponsivePage>
  );
};

export default GenerateVideoPage;
